import React, {ReactElement, useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef} from 'react';
import type {MenuListProps, GroupBase} from 'react-select';

const OPTION_HEIGHT = 35; // Estimate for a row not yet measured; a wrapped label is taller (OWA-988)
const BUFFER_SIZE = 5; // Extra items to render above/below viewport
const VIRTUALIZATION_THRESHOLD = 100; // Only virtualize lists with more than this many items
const SIZER_COUNT = 3; // Options with the widest text, rendered hidden to set the menu's width

/** Index of the last row whose top is at or above `y`. */
function rowAt(offsets: number[], y: number): number {
    let lo = 0;
    let hi = offsets.length - 2;
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (offsets[mid] <= y) lo = mid;
        else hi = mid - 1;
    }
    return Math.max(0, lo);
}

/**
 * Simple virtualized MenuList component for react-select.
 * Uses basic scroll-based virtualization instead of react-window
 * for better compatibility with react-select's menu portal.
 */
function VirtualizedMenuList<
    Option,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>
>(props: MenuListProps<Option, IsMulti, Group>) {
    const {children, maxHeight, innerRef} = props;

    // Convert children to an array before choosing a scroll position. React
    // Select supplies `isSelected` on each option child, including options
    // outside the initial virtual viewport.
    const childrenArray = useMemo(
        () => React.Children.toArray(children) as ReactElement[],
        [children]
    );
    const selectedOptions = props.getValue();
    const selectedIndex = useMemo(
        () => childrenArray.findIndex(child => {
            const optionProps = child.props as {data?: Option, isSelected?: boolean};
            return optionProps.isSelected || selectedOptions.includes(optionProps.data as Option);
        }),
        [childrenArray, selectedOptions],
    );
    const focusedIndex = useMemo(
        () => childrenArray.findIndex(child => Boolean((child.props as {isFocused?: boolean}).isFocused)),
        [childrenArray],
    );

    // Determine whether to virtualize before deriving the current viewport.
    const shouldVirtualize = childrenArray.length >= VIRTUALIZATION_THRESHOLD;

    // Rows are positioned from their measured heights, so a label that wraps onto a second
    // line pushes the rows below it down instead of overlapping them (OWA-988). Heights are
    // keyed by the option's React key so they survive filtering.
    const [heights, setHeights] = useState<ReadonlyMap<string, number>>(() => new Map());
    const offsets = useMemo(() => {
        const result = [0];
        for (const child of childrenArray) {
            result.push(result[result.length - 1] + (heights.get(String(child.key)) ?? OPTION_HEIGHT));
        }
        return result;
    }, [childrenArray, heights]);
    const totalHeight = offsets[offsets.length - 1];
    const visibleHeight = Math.min(maxHeight, totalHeight);

    // React Compiler wraps ref values in a Proxy for render-time access
    // tracking. react-select's class component stores the DOM element via
    // `innerRef` and later calls `scrollTo` / `scrollIntoView` on it.
    // When the call goes through a Proxy, `this` isn't a real Element and
    // the browser throws "Illegal invocation". Pre-binding `scrollTo` on
    // the raw DOM node before passing it to react-select ensures the native
    // method always has the correct `this`.
    const handleRef = useCallback((node: HTMLDivElement | null) => {
        if (node && typeof node.scrollTo === 'function') {
            const bound = node.scrollTo.bind(node);
            Object.defineProperty(node, 'scrollTo', {
                value: bound,
                writable: true,
                configurable: true,
            });
        }
        if (typeof innerRef === 'function') {
            innerRef(node);
        } else if (innerRef && typeof innerRef === 'object') {
            // eslint-disable-next-line react-hooks/immutability, react-compiler/react-compiler -- innerRef is a MutableRefObject; setting .current is react-select's documented contract (MenuListProps.innerRef). react-compiler skip acknowledged — the mutation is unavoidable here.
            (innerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
    }, [innerRef]);

    const [scrollTop, setScrollTop] = useState(0);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        setScrollTop(e.currentTarget.scrollTop);
    }, []);

    // Measure the rendered rows after every render; a changed height re-renders once more.
    // The options' font is read here too, for ranking the hidden width rows below.
    //
    // A row measured above the anchor moves everything below it, so the list scrolls by the
    // same amount once the new offsets are rendered (OWA-988). The anchor is the focused or selected
    // row while it is in view, so a jump to it (End, opening on a late selection) stays on
    // it; otherwise it is the top of the view, so the rows in view stay put.
    const spacerRef = useRef<HTMLDivElement | null>(null);
    const pendingScrollTop = useRef<number | null>(null);
    const [font, setFont] = useState('');
    const measureRows = useCallback(() => {
        const spacer = spacerRef.current;
        const list = spacer?.parentElement;
        const option = spacer?.querySelector<HTMLElement>(':scope > [data-row-key] > *');
        if (option) {
            const style = getComputedStyle(option);
            setFont(`${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`);
        }
        const rows = [...(spacer?.querySelectorAll<HTMLElement>(':scope > [data-row-key]') ?? [])];
        if (list) {
            const target = rows.find(row => row.dataset.rowTarget !== undefined);
            const targetInView = target && target.offsetTop >= list.scrollTop && target.offsetTop < list.scrollTop + list.clientHeight;
            const anchor = targetInView ? target.offsetTop : list.scrollTop;
            // A target that was fully in view and grew past the bottom (its label re-wrapped)
            // scrolls up just enough to show it again, never past its own top.
            const viewBottom = list.scrollTop + list.clientHeight;
            let shift = target && targetInView && target.offsetTop + Number(target.dataset.rowHeight) <= viewBottom
                ? Math.min(Math.max(0, target.offsetTop + target.offsetHeight - viewBottom), target.offsetTop - list.scrollTop)
                : 0;
            for (const row of rows) {
                if (row.offsetTop < anchor) shift += row.offsetHeight - Number(row.dataset.rowHeight);
                // Counted once: a second measure before the next render sees no change.
                row.dataset.rowHeight = String(row.offsetHeight);
            }
            // Kept as a position, not a delta: the browser already clamps scrollTop when the
            // content shrinks, and a delta would move the view a second time.
            if (shift) pendingScrollTop.current = (pendingScrollTop.current ?? list.scrollTop) + shift;
        }
        setHeights(prev => {
            let next: Map<string, number> | null = null;
            for (const row of rows) {
                const key = row.dataset.rowKey ?? '';
                if (prev.get(key) !== row.offsetHeight) (next ??= new Map(prev)).set(key, row.offsetHeight);
            }
            return next ?? prev;
        });
    }, []);
    useLayoutEffect(() => {
        const list = spacerRef.current?.parentElement;
        if (list && pendingScrollTop.current !== null) list.scrollTo({top: pendingScrollTop.current, behavior: 'instant'});
        pendingScrollTop.current = null;
        measureRows();
    });

    // A width change re-wraps the labels, so re-measure when the list resizes.
    useEffect(() => {
        const spacer = spacerRef.current;
        if (!spacer || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measureRows);
        observer.observe(spacer);
        return () => observer.disconnect();
    }, [measureRows, shouldVirtualize]);

    // Calculate virtualization params (always calculate to avoid conditional hook behavior)
    const maxScrollTop = Math.max(0, totalHeight - visibleHeight);
    const clampedScrollTop = Math.min(Math.max(0, scrollTop), maxScrollTop);
    const startIndex = Math.max(0, rowAt(offsets, clampedScrollTop) - BUFFER_SIZE);
    const endIndex = Math.min(childrenArray.length, rowAt(offsets, clampedScrollTop + visibleHeight) + 1 + BUFFER_SIZE);

    // Build visible items array (only used when virtualizing). Render the
    // selected or focused row even if it falls outside the viewport so
    // Select.tsx can measure it and scroll only this menu list into view.
    const visibleItems = useMemo(() => {
        if (!shouldVirtualize) return null;

        const indices = Array.from({length: endIndex - startIndex}, (_, i) => startIndex + i);
        if (selectedIndex >= 0 && !indices.includes(selectedIndex)) indices.push(selectedIndex);
        if (focusedIndex >= 0 && !indices.includes(focusedIndex)) indices.push(focusedIndex);

        const target = focusedIndex >= 0 ? focusedIndex : selectedIndex;
        const items = [];
        for (const i of indices) {
            items.push(
                <div
                    key={i}
                    data-row-key={String(childrenArray[i].key)}
                    data-row-height={offsets[i + 1] - offsets[i]}
                    data-row-target={i === target ? '' : undefined}
                    style={{
                        position: 'absolute',
                        top: offsets[i],
                        left: 0,
                        right: 0,
                    }}
                >
                    {childrenArray[i]}
                </div>
            );
        }
        return items;
    }, [shouldVirtualize, startIndex, endIndex, selectedIndex, focusedIndex, childrenArray, offsets]);

    // Absolutely positioned rows do not widen the menu, so the menu sizes itself to hidden
    // copies of the options with the widest text (OWA-988): the label when it is a string,
    // otherwise the value, which wrappers with element labels fill with the search text.
    // Text width stands in for rendered width so that opening a long menu stays fast; a
    // formatted label that renders wider than its text can still wrap early, an accepted
    // limit (John, 2026-10-05). A group contributes its options, never a copy of itself.
    // The copies drop React Select's id, role and handlers so nothing can focus, select or
    // find them.
    const sizerItems = useMemo(() => {
        if (!shouldVirtualize) return null;

        const context = document.createElement('canvas').getContext('2d');
        if (context && font) context.font = font;
        const widthOf = (child: ReactElement) => {
            const {label, data} = child.props as {label?: unknown, data?: {value?: unknown}};
            // The option collapses runs of spaces, tabs and line breaks and drops them at the
            // ends, so the measure does too; non-breaking spaces stay, as they do on screen.
            const text = (typeof label === 'string' ? label : String(data?.value ?? '')).replace(/[ \t\n\f\r]+/g, ' ').replace(/^ | $/g, '');
            return context?.measureText(text).width ?? text.length;
        };
        const isOption = (child: ReactElement) => (child.props as {type?: string}).type === 'option';
        return childrenArray
            .flatMap(child => isOption(child)
                ? [child]
                : (React.Children.toArray((child.props as {children?: React.ReactNode}).children) as ReactElement[]).filter(isOption))
            .map(child => [widthOf(child), child] as const)
            .sort((a, b) => b[0] - a[0])
            .slice(0, SIZER_COUNT)
            .map(([, child]) => React.cloneElement(child as ReactElement<Record<string, unknown>>, {
                key: `sizer-${child.key}`,
                innerProps: {},
                innerRef: undefined,
                isFocused: false,
                isSelected: false,
            }));
    }, [shouldVirtualize, childrenArray, font]);

    // For small lists, render normally without virtualization
    if (!shouldVirtualize) {
        return (
            <div
                ref={handleRef}
                className="react-select__menu-list"
                role="listbox"
                style={{maxHeight, overflowY: 'auto'}}
                onScroll={handleScroll}
            >
                {children}
            </div>
        );
    }

    // Render virtualized list
    return (
        <div
            ref={handleRef}
            className="react-select__menu-list"
            role="listbox"
            aria-multiselectable={false}
            style={{
                maxHeight,
                overflowY: 'auto',
                position: 'relative',
            }}
            onScroll={handleScroll}
        >
            {/* Spacer div to create correct scroll height */}
            <div ref={spacerRef} style={{height: totalHeight, position: 'relative'}}>
                {visibleItems}
            </div>
            <div aria-hidden={true} style={{height: 0, overflow: 'hidden', visibility: 'hidden'}}>
                {sizerItems}
            </div>
        </div>
    );
}

export default VirtualizedMenuList;
